import React, { useEffect, useState } from "react";
import { Loader2, Phone, SkipForward, TriangleAlert, User } from "lucide-react";
import { usePowerDialer } from "@/context/PowerDialerContext";
import { useGetDialerQueueCountsQuery, useGetPreviewLeadQuery } from "@/store/rest_api";
import { getContactDisplayName, getInitials } from "@/util/string";
import { emptyQueueMessage } from "@/util/dialer";
import { DialerQueueGroup } from "@/enum/DialerQueueGroup";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function PreviewModePane() {
  const { campaign, isDialing, lineCalls, callLeadAnyway, skipContact } = usePowerDialer();
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [isCalling, setIsCalling] = useState(false);
  const campaignId = campaign?.id ?? "";
  const hasLiveCall = lineCalls.length > 0;
  const isPreviewPaused = !campaignId || !isDialing || hasLiveCall;
  const {
    data: nextLead,
    isFetching,
    refetch: refetchPreviewLead,
  } = useGetPreviewLeadQuery(campaignId, { skip: isPreviewPaused });
  const { data: queueCounts } = useGetDialerQueueCountsQuery(campaignId, { skip: !campaignId });
  const dueNow = queueCounts?.groups[DialerQueueGroup.DUE_NOW] ?? 0;

  const handleDial = async (acknowledgedCallingRules: boolean) => {
    if (!nextLead) {
      return;
    }
    setIsCalling(true);
    try {
      setConfirmation(await callLeadAnyway({ id: nextLead.id }, acknowledgedCallingRules));
    } finally {
      setIsCalling(false);
    }
  };

  useEffect(() => {
    if (!isPreviewPaused && !nextLead && !isFetching && dueNow > 0) {
      void refetchPreviewLead();
    }
  }, [dueNow]);

  if (hasLiveCall || !isDialing) {
    return null;
  }

  if (!nextLead) {
    if (isFetching) {
      return null;
    }
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{emptyQueueMessage(queueCounts?.nextDueAt)}</p>
      </Card>
    );
  }

  const contact = nextLead.contact;
  const displayName = (contact && getContactDisplayName(contact)) ?? "Unknown";
  const initials = getInitials(contact?.firstName, contact?.lastName);
  const phones = contact?.phones ?? [];
  const priorCalls = nextLead.retryCount ?? 0;

  return (
    <Card className="p-6">
      <div className="space-y-4 text-center">
        <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Up next</div>
        <div className="flex justify-center">
          <Avatar className="h-14 w-14">
            {contact?.photo && <AvatarImage src={contact.photo} alt={displayName} />}
            <AvatarFallback className="bg-primary/10 font-semibold text-primary">
              {initials !== "?" ? initials : <User className="h-5 w-5" />}
            </AvatarFallback>
          </Avatar>
        </div>
        <div>
          <div className="text-lg font-semibold dark:text-white">{displayName}</div>
          {contact?.company && <div className="text-sm text-muted-foreground">{contact.company}</div>}
        </div>

        <div className="space-y-1 text-sm">
          {phones.length === 0 && <div className="text-muted-foreground">{nextLead.phoneNumber}</div>}
          {phones.map((phone) => {
            const isDialedNumber =
              phone.phoneNumberE164 === nextLead.phoneNumber || phone.phoneNumber === nextLead.phoneNumber;
            return (
              <div
                key={phone.id ?? phone.phoneNumberE164}
                className={isDialedNumber ? "font-semibold dark:text-white" : "text-muted-foreground"}
              >
                {phone.phoneNumber ?? phone.phoneNumberE164}
                {isDialedNumber ? " · dialing this number" : ""}
              </div>
            );
          })}
        </div>

        {(priorCalls > 0 || nextLead.disposition) && (
          <div className="text-sm text-muted-foreground">
            {priorCalls > 0 && `Called ${priorCalls} ${priorCalls === 1 ? "time" : "times"} before`}
            {priorCalls > 0 && nextLead.disposition && " · "}
            {nextLead.disposition && `Last outcome: ${nextLead.disposition}`}
          </div>
        )}

        {nextLead.callingNote && (
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-soft/50 p-3 text-left text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{nextLead.callingNote}</span>
          </div>
        )}

        <div className="flex justify-center gap-3">
          <Button disabled={isCalling} onClick={() => void handleDial(false)}>
            {isCalling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
            Dial
          </Button>
          <Button variant="outline" disabled={isCalling} onClick={() => skipContact({ id: nextLead.id })}>
            <SkipForward className="mr-2 h-4 w-4" />
            Skip
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmation !== null} onOpenChange={(open) => !open && setConfirmation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Call this lead now?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation} The call is recorded with your name and the lead's local time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDial(true)}>Call anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
